import {
  APP_ZONES,
  CommonPageConfig,
  ContainerType,
  cryptoRandomUUID,
  RelevantBaasTags,
  WorkflowPageConfig,
  WorkflowUserType,
  WorkflowWidgetConfig,
} from '@finxone-platform/shared/sys-config-types';
import { generateOnboardingDefaultPage } from '../../defaults/pages/onboarding.pages';
import { buildDefaultVerificationPages } from '../../defaults/pages/verification.pages';
import {
  defaultModalConfiguration,
  defaultPageConfigurations,
} from '../../defaults/widgets/workflow-ui-constants.v2.type';
import { defaultAccountsPages } from '../../defaults/zones/accounts/accounts-page-setups';
import { defaultLandingPages } from '../../defaults/zones/landing/landing-page-setups';
import { defaultOrganisationAnonymousRolePagesConfig } from '../../defaults/zones/organisation-user/onboarding-organisation/onboarding-organisation';
import { defaultOrganisationProfilePages } from '../../defaults/zones/organisation-user/organisation-profile/org-profile-page-setups';
import { buildMobileVerificationOrgZonePagesConfig } from '../../defaults/zones/organisation-user/verification-organisation/verification-organisation-mobile-page-setup/verification-organisation-mobile-page-setup';
import {
  enterEmailWidgets,
  passwordCheckEmailWidgets,
  passwordForgotWidgets,
  passwordResetValidationWidgets,
} from '../../defaults/zones/profile/widget-sections/password';
import { removeSpaceFromRole } from '../../mapping/utils/zone-mapping.utils';
export type PageConfig = {
  name: string;
  title: string;
  url?: string;
  widgets?: WorkflowWidgetConfig[];
  isRemovable?: boolean;
  pageConfiguration?: CommonPageConfig;
  tags: string[];
  packs?: string[];
  pageUniqueId?: string;
};

export function getLandingZoneDefaultPage(): WorkflowPageConfig[] {
  const landingDefaultPageComponent = [
    ...buildDynamicZoneConfig(defaultLandingPages),
  ] as WorkflowPageConfig[];
  return landingDefaultPageComponent;
}

export const generalDefaultPageComponent = {
  name: APP_ZONES.GENERAL,
  title: 'General Zone',
  pageUniqueId: cryptoRandomUUID(),
  pageConfiguration: {
    mainComponentPageName: 'ParentContainerComponent',
    isRemovable: false,
    pageType: 'dynamicGrid',
    url: 'general',
    ...defaultPageConfigurations,
  },
  widgets: [],
} as WorkflowPageConfig;

export function getAccountZoneDefaultPage(): WorkflowPageConfig[] {
  const accountDefaultPageComponent = [
    ...buildDynamicZoneConfig(defaultAccountsPages),
  ] as WorkflowPageConfig[];

  return accountDefaultPageComponent;
}

export function getSignOutZoneDefaultPage(): WorkflowPageConfig[] {
  const forgotPassWordDefaultPageConfigMobile = [
    ...buildDynamicZoneConfig([
      {
        name: 'enter-email',
        title: 'Enter Email',
        widgets: enterEmailWidgets('mobile'),
        url: 'enter-email',
      },
      {
        name: 'pass-email-check',
        title: 'Password Check Email',
        widgets: passwordCheckEmailWidgets('mobile'),
        url: 'password-check-email',
      },
      {
        name: 'pass-forgot',
        title: 'Forgot Password',
        widgets: passwordForgotWidgets('mobile'),
        url: 'forgot-password',
      },
      {
        name: 'pass-reset-validation',
        title: 'Password Reset Validation',
        widgets: passwordResetValidationWidgets('mobile'),
        url: 'pass-reset-validation',
      },
    ]),
  ] as WorkflowPageConfig[];
  return forgotPassWordDefaultPageConfigMobile;
}

export const commonPageComponentsForMobileAndDesktopAndTablet = new Map<
  string,
  ({
    userType,
    relevantBaasTags,
  }: {
    userType?: WorkflowUserType[];
    relevantBaasTags: RelevantBaasTags[];
  }) => WorkflowPageConfig[]
>([
  [
    APP_ZONES.PROFILE,
    () => {
      return [];
    },
  ],
  [
    APP_ZONES.ONBOARDING,
    ({ userType }) => {
      return [generateOnboardingDefaultPage(userType)];
    },
  ],
  [
    APP_ZONES.VERIFICATION,
    ({ userType }) => {
      return buildDynamicZoneConfig(
        buildDefaultVerificationPages({
          userType: userType as string[],
          deviceType: 'mobile',
        }),
      );
    },
  ],
  [
    APP_ZONES.LANDING,
    () => {
      return [...getLandingZoneDefaultPage()];
    },
  ],
  [
    APP_ZONES.GENERAL,
    () => {
      return [generalDefaultPageComponent];
    },
  ],
  [
    APP_ZONES.PAYMENT,
    () => {
      return [];
    },
  ],
  [
    APP_ZONES.ACCOUNTS_ZONE,
    () => {
      return [...getAccountZoneDefaultPage()];
    },
  ],
  [
    APP_ZONES.SIGNED_OUT_FORGOT_PASSWORD,
    () => {
      return [...getSignOutZoneDefaultPage()];
    },
  ],
  [
    APP_ZONES.CARD_MANAGEMENT_ZONE,
    () => {
      return [];
    },
  ],
]);

export const guestLandingPageConfigMobile = new Map<
  string,
  ({
    userType,
    relevantBaasTags,
  }: {
    userType?: string[];
    relevantBaasTags: RelevantBaasTags[];
  }) => WorkflowPageConfig[]
>([]);

export const zoneToMobilePageComponentDefaults = new Map<
  string,
  ({
    userType,
    relevantBaasTags,
  }: {
    userType?: WorkflowUserType[];
    relevantBaasTags: RelevantBaasTags[];
  }) => WorkflowPageConfig[]
>([
  ...commonPageComponentsForMobileAndDesktopAndTablet,
  ...guestLandingPageConfigMobile,
  [
    APP_ZONES.ORGANISATION_ONBOARDING,
    () => {
      return buildOrganisationOnboardingConfig();
    },
  ],
  [
    APP_ZONES.ORGANISATION_VERIFICATION,
    () => {
      return [...buildMobileVerificationOrgZonePagesConfig()];
    },
  ],
  [
    APP_ZONES.ORGANISATION_PROFILE,
    () => {
      return [...getBusinessProfileZoneDefaultPage()];
    },
  ],
]);

export function buildOrganisationOnboardingConfig(isDesktop = false): WorkflowPageConfig[] {
  return defaultOrganisationAnonymousRolePagesConfig(isDesktop);
}

export function getBusinessProfileZoneDefaultPage(): WorkflowPageConfig[] {
  const buildOrganisationProfileConfig = [
    ...buildDynamicZoneConfig(defaultOrganisationProfilePages),
  ] as WorkflowPageConfig[];
  return buildOrganisationProfileConfig;
}

export function buildDynamicZoneConfig(
  pageConfig: {
    name: string;
    title: string;
    url?: string;
    tags?: string[];
    containerType?: ContainerType;
    widgets?: WorkflowWidgetConfig[];
    isRemovable?: boolean;
    pageConfiguration?: CommonPageConfig;
    pageUniqueId?: string;
  }[],
): WorkflowPageConfig[] {
  const pagesConfig: WorkflowPageConfig[] = [];
  pageConfig.forEach((page) => {
    const pageObj: WorkflowPageConfig = {
      name: page.name,
      title: page.title,
      widgets: page.widgets ?? [],
      containerType: page?.containerType ?? ContainerType.PAGE,
      pageConfiguration: {
        url: page.url ?? removeSpaceFromRole(page.name),
        pageType: 'dynamicGrid',
        isRemovable: page.isRemovable ?? false,
        mainComponentPageName: 'ParentContainerComponent',
        modalConfiguration: {
          ...{
            url: page.url ?? removeSpaceFromRole(page.name),
            pageType: 'dynamicGrid',
            isRemovable: page.isRemovable ?? false,
            mainComponentPageName: 'PageAsModalComponent',
          },
          ...defaultModalConfiguration,
          ...page?.pageConfiguration?.modalConfiguration,
        },
        ...defaultPageConfigurations,
        ...page.pageConfiguration,
      },
      tags: page?.tags,
      pageUniqueId: cryptoRandomUUID(),
    };
    pagesConfig.push(pageObj);
  });

  return pagesConfig;
}
